import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Send, ChevronDown } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useSite } from "../context/SiteContext";
import { sendPublicContact } from "../lib/api";
import { getCaptchaToken } from "../lib/captcha";

/** Parse a multi-line FAQ string into Q/A blocks.
 * Convention (admin enters this in the CMS):
 *   Q: How long until I get my order?
 *   A: 1-3 working days inside Portugal.
 *
 *   Q: Do you ship abroad?
 *   A: Yes, EU and UK.
 * Blank line separates entries. Lines that don't start with Q:/A: are appended
 * to the previous answer so admins can write multi-line answers.
 */
function parseFaq(raw) {
  if (!raw) return [];
  const out = [];
  let cur = null;
  raw.split("\n").forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.toUpperCase().startsWith("Q:")) {
      if (cur) out.push(cur);
      cur = { q: trimmed.slice(2).trim(), a: "" };
    } else if (trimmed.toUpperCase().startsWith("A:")) {
      if (cur) cur.a = trimmed.slice(2).trim();
    } else if (cur && trimmed) {
      cur.a += (cur.a ? "\n" : "") + trimmed;
    }
  });
  if (cur) out.push(cur);
  return out;
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-kult-border" data-testid="faq-item">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left hover:text-kult-pink transition-colors"
        data-testid="faq-question"
      >
        <span className="font-display text-xl">{q}</span>
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p className="pb-5 text-kult-muted whitespace-pre-line" data-testid="faq-answer">{a}</p>
      )}
    </div>
  );
}

function ContactForm({ lang }) {
  const [form, setForm] = useState({ name: "", email: "", order_number: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true); setSent(false);
    try {
      const captcha_token = await getCaptchaToken("contact");
      await sendPublicContact({
        name: form.name,
        email: form.email,
        order_number: form.order_number || null,
        message: form.message,
        captcha_token,
      });
      setSent(true);
      setForm({ name: "", email: "", order_number: "", message: "" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Send failed");
    }
    setSubmitting(false);
  };

  const t = lang === "pt"
    ? { name: "NOME", email: "EMAIL", order: "Nº ENCOMENDA (opcional)", msg: "MENSAGEM", submit: "ENVIAR", ok: "⚡ Recebemos a tua mensagem. Voltamos rapidamente." }
    : { name: "NAME", email: "EMAIL", order: "ORDER NUMBER (optional)", msg: "MESSAGE", submit: "SEND", ok: "⚡ Got it. We'll get back to you soon." };

  return (
    <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 max-w-3xl" data-testid="public-contact-form">
      <label className="block">
        <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{t.name}</span>
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" data-testid="public-contact-name" />
      </label>
      <label className="block">
        <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{t.email}</span>
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" data-testid="public-contact-email" />
      </label>
      <label className="block md:col-span-2">
        <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{t.order}</span>
        <input value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" data-testid="public-contact-order" placeholder="KULT-20260101-ABC123" />
      </label>
      <label className="block md:col-span-2">
        <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-2">{t.msg}</span>
        <textarea required minLength={2} rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none" data-testid="public-contact-message" />
      </label>
      {error && <p className="md:col-span-2 font-mono text-xs text-kult-pink">{error}</p>}
      {sent && <p className="md:col-span-2 font-mono text-xs text-kult-yellow" data-testid="public-contact-success">{t.ok}</p>}
      <button type="submit" disabled={submitting} className="md:col-span-2 justify-self-start bg-kult-pink text-black font-display text-xl px-8 py-3 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center gap-2" data-testid="public-contact-submit">
        <Send size={16} /> {submitting ? "..." : t.submit}
      </button>
    </form>
  );
}

export default function HelpPage({ page }) {
  const { lang } = useApp();
  const { s } = useSite();

  const tabs = [
    { key: "faq", to: "/help/faq", labelEn: "FAQ", labelPt: "FAQ" },
    { key: "delivery", to: "/help/delivery", labelEn: "DELIVERY INFO", labelPt: "INFO DE ENVIO" },
    { key: "contact", to: "/help/contact", labelEn: "CONTACT", labelPt: "CONTACTO" },
  ];

  const pageTitle = (() => {
    if (page === "delivery") return s(`help_delivery_title`, lang === "pt" ? "INFO DE ENVIO" : "DELIVERY INFO");
    if (page === "contact") return s(`help_contact_title`, lang === "pt" ? "CONTACTA-NOS" : "CONTACT US");
    return s(`help_faq_title`, "FAQ");
  })();
  const pageIntro = (() => {
    if (page === "delivery") return s("help_delivery_intro", "");
    if (page === "contact") return s("help_contact_intro", "");
    return s("help_faq_intro", "");
  })();

  return (
    <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-12 md:py-20" data-testid={`page-help-${page}`}>
      <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ HELP</p>
      <h1 className="font-display text-5xl md:text-7xl mt-2 leading-none">
        {pageTitle.split(" ").slice(0, -1).join(" ")} <span className="text-kult-pink">{pageTitle.split(" ").slice(-1)[0]}</span>
      </h1>
      {pageIntro && <p className="text-kult-muted mt-3 max-w-2xl whitespace-pre-line">{pageIntro}</p>}

      {/* Tabs */}
      <div className="flex gap-1 mt-8 mb-8 border-b border-kult-border overflow-x-auto" data-testid="help-tabs">
        {tabs.map((tab) => (
          <Link
            key={tab.key}
            to={tab.to}
            className={`px-5 py-3 font-mono text-[11px] tracking-[0.3em] whitespace-nowrap ${page === tab.key ? "text-kult-pink border-b-2 border-kult-pink -mb-px" : "text-kult-muted hover:text-white"}`}
            data-testid={`help-tab-${tab.key}`}
          >
            {lang === "pt" ? tab.labelPt : tab.labelEn}
          </Link>
        ))}
      </div>

      {/* Content */}
      {page === "faq" && (
        <div className="max-w-3xl" data-testid="help-content-faq">
          {parseFaq(s("help_faq_body", "")).length === 0 ? (
            <p className="font-mono text-xs text-kult-muted">No FAQs yet. Admins can add them in <strong>SITE COPY → HELP PAGES</strong>.</p>
          ) : (
            parseFaq(s("help_faq_body", "")).map((qa, i) => <FaqItem key={i} q={qa.q} a={qa.a} />)
          )}
        </div>
      )}

      {page === "delivery" && (
        <div className="max-w-3xl prose prose-invert" data-testid="help-content-delivery">
          <p className="whitespace-pre-line text-white leading-relaxed">
            {s("help_delivery_body", lang === "pt"
              ? "Adiciona informação de envio no admin → SITE COPY → HELP PAGES."
              : "Add delivery info in admin → SITE COPY → HELP PAGES.")}
          </p>
        </div>
      )}

      {page === "contact" && (
        <div className="max-w-3xl" data-testid="help-content-contact">
          {s("help_contact_body", "") && (
            <p className="whitespace-pre-line text-white leading-relaxed mb-10">{s("help_contact_body", "")}</p>
          )}
          <ContactForm lang={lang} />
        </div>
      )}
    </div>
  );
}
